import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Plus, Save, Check, Pencil, Trash2, X } from 'lucide-react';
import { CATEGORY_LABEL, fmt } from './constants';
import { MiniStat } from './StatCards';

const usabilityBadgeClass = {
  usable: 'bg-emerald-100 text-emerald-800',
  broken: 'bg-orange-100 text-orange-800',
  not_usable: 'bg-rose-100 text-rose-800',
};
const usabilityLabelMap = {
  usable: 'Usable', broken: 'Broken', not_usable: 'Not usable',
};

const BreakDialog = ({
  open, onOpenChange,
  breakPiece, breakForm, setBreakForm, liveCalc,
  onOpenAddEntry, onOpenEditEntry, onDeleteEntry,
  onSaveDraft, onConfirm,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[95vh] overflow-hidden flex flex-col p-0">
      <DialogHeader className="px-4 pt-4">
        <DialogTitle className="flex items-center gap-2 flex-wrap text-base">
          <span>Break {breakPiece?.serial_no}</span>
          {breakPiece?.breakage_draft && <Badge className="bg-yellow-100 text-yellow-800">Draft loaded</Badge>}
        </DialogTitle>
        <DialogDescription>Enter current net weight (shop share only) and worker gold. Net gold, fine gold and weight lost are calculated automatically.</DialogDescription>
      </DialogHeader>
      {breakForm && breakPiece && liveCalc && (
        <div className="overflow-y-auto flex-1 px-4 pb-4 pt-2 space-y-4">
          <div className="text-xs text-slate-500">
            Branch: <b>{breakPiece.branch}</b> · Gross: <b>{fmt(breakPiece.gross_weight)} gm</b> · Purity: <b>{fmt(breakPiece.expected_purity, 2)}%</b>
          </div>

          {/* Reconciliation strip — only shown when piece has expected counts */}
          {breakPiece.expected_stones && (
            (breakPiece.expected_stones.diamond + breakPiece.expected_stones.stone + breakPiece.expected_stones.beads_pearl) > 0
          ) && (
            <div className="grid grid-cols-3 gap-2" data-testid="break-reconciliation">
              {['diamond', 'stone', 'beads_pearl'].map((cat) => {
                const exp = breakPiece.expected_stones[cat] || 0;
                const rec = (breakForm.stone_entries || [])
                  .filter(e => e.category === cat)
                  .reduce((s, e) => s + (parseInt(e.pcs, 10) || 0), 0);
                let tone = 'bg-slate-50 text-slate-700 border-slate-200';
                if (exp > 0) {
                  if (rec === exp) tone = 'bg-emerald-50 text-emerald-800 border-emerald-200';
                  else if (rec === 0) tone = 'bg-amber-50 text-amber-800 border-amber-200';
                  else if (rec < exp) tone = 'bg-rose-50 text-rose-800 border-rose-200';
                  else tone = 'bg-orange-50 text-orange-800 border-orange-200';
                }
                return (
                  <div key={cat} className={`rounded-lg border py-1.5 text-center ${tone}`} data-testid={`break-recon-${cat}`}>
                    <div className="text-[10px] uppercase tracking-wide opacity-80">{CATEGORY_LABEL[cat]}</div>
                    <div className="text-sm font-semibold">{rec} / {exp}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Weight inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Current Net Wt (gm)</Label>
              <Input
                type="number" inputMode="decimal" step="0.001" min="0"
                value={breakForm.current_net_weight}
                onChange={(e) => setBreakForm({ ...breakForm, current_net_weight: e.target.value })}
                data-testid="break-current-net-input"
                className="h-11 text-base"
                placeholder="After stones removed"
              />
            </div>
            <div>
              <Label className="text-xs">Worker Gold (gm)</Label>
              <Input
                type="number" inputMode="decimal" step="0.001" min="0"
                value={breakForm.worker_gold_wt}
                onChange={(e) => setBreakForm({ ...breakForm, worker_gold_wt: e.target.value })}
                data-testid="break-worker-gold-input"
                className="h-11 text-base"
                placeholder="Given to worker"
              />
            </div>
          </div>

          {/* Live compute strip */}
          <div className="grid grid-cols-4 gap-2 text-center">
            <MiniStat label="Net Gold" value={`${fmt(liveCalc.netGold)}`} tone="emerald" testid="break-live-net-gold" />
            <MiniStat label="Fine (total)" value={`${fmt(liveCalc.fineTotal)}`} tone="amber" testid="break-live-fine-gold" />
            <MiniStat label="Fine (shop)" value={`${fmt(liveCalc.fineShop)}`} tone="amber" testid="break-live-fine-shop" />
            <MiniStat label="Wt Lost" value={`${fmt(liveCalc.weightLost)}`} tone="rose" testid="break-live-weight-lost" />
          </div>

          {/* Stone entries list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">Stones / Diamonds / Beads</Label>
              <Button size="sm" onClick={onOpenAddEntry} className="h-9 bg-amber-700 hover:bg-amber-800" data-testid="break-add-entry-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <div className="border rounded-lg divide-y" data-testid="break-entries-list">
              {breakForm.stone_entries.length === 0 && (
                <div className="text-center py-4 text-slate-400 text-xs">No entries yet · tap Add</div>
              )}
              {breakForm.stone_entries.map((e, i) => {
                const isWeightOnlyEntry = e.category === 'dust' || e.category === 'others';
                const usability = e.usability || (e.usable ? 'usable' : 'not_usable');
                return (
                  <div key={i} className="p-2.5 flex items-center gap-2 text-sm" data-testid={`break-entry-${i}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{CATEGORY_LABEL[e.category] || e.category}</span>
                        {!isWeightOnlyEntry && (
                          <Badge className={usabilityBadgeClass[usability]}>{usabilityLabelMap[usability]}</Badge>
                        )}
                        {isWeightOnlyEntry && (
                          <Badge className="bg-slate-100 text-slate-600">weight-only</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {isWeightOnlyEntry ? `${fmt(e.wt)} gm` : `${e.pcs || 0} pcs · ${fmt(e.wt)} gm`}
                      </div>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => onOpenEditEntry(i)} data-testid={`break-entry-edit-${i}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => onDeleteEntry(i)} data-testid={`break-entry-delete-${i}`}>
                      <Trash2 className="w-4 h-4 text-rose-600" />
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Total stones: <b>{fmt(liveCalc.stoneWt)} gm</b>
            </div>
          </div>

          <div>
            <Label className="text-xs">Remarks</Label>
            <Textarea rows={2} value={breakForm.remarks}
              onChange={(e) => setBreakForm({ ...breakForm, remarks: e.target.value })}
              data-testid="break-remarks-input" />
          </div>
        </div>
      )}
      {/* Soft reconciliation note above action bar — never blocks confirm */}
      {breakForm && breakPiece && breakPiece.expected_stones && (() => {
        const label = { diamond: 'Diamonds', stone: 'Stones', beads_pearl: 'Beads & Pearls' };
        const diffs = [];
        for (const cat of ['diamond', 'stone', 'beads_pearl']) {
          const exp = breakPiece.expected_stones[cat] || 0;
          if (exp === 0) continue;
          const rec = (breakForm.stone_entries || [])
            .filter(e => e.category === cat)
            .reduce((s, e) => s + (parseInt(e.pcs, 10) || 0), 0);
          if (rec === exp) continue;
          if (rec < exp) diffs.push(`${label[cat]} ${exp - rec} short`);
          else diffs.push(`${label[cat]} ${rec - exp} extra`);
        }
        if (!diffs.length) return null;
        return (
          <div className="border-t px-4 py-2 bg-amber-50 text-xs text-amber-800" data-testid="break-soft-note">
            <b>Heads-up:</b> {diffs.join(' · ')}. This will be recorded in the audit report — Confirm still proceeds.
          </div>
        );
      })()}
      <div className="border-t p-3 flex gap-2 bg-white">
        <Button variant="outline" onClick={() => onOpenChange(false)} className="h-11 flex-1">
          <X className="w-4 h-4 mr-1" /> Close
        </Button>
        <Button variant="outline" onClick={onSaveDraft} className="h-11 flex-1" data-testid="break-save-draft-btn">
          <Save className="w-4 h-4 mr-1" /> Draft
        </Button>
        <Button onClick={onConfirm} className="h-11 flex-1 bg-emerald-700 hover:bg-emerald-800" data-testid="break-confirm-btn">
          <Check className="w-4 h-4 mr-1" /> Confirm
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

export default BreakDialog;
